import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Paper,
  Divider,
  IconButton,
  Collapse,
  alpha,
  styled,
  useTheme,
  CircularProgress,
  Badge,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from '@mui/material';
import {
  Agriculture,
  VideoCall,
  WhatsApp,
  Schedule,
  Person,
  LocationOn,
  Email,
  Phone,
  CalendarToday,
  AccessTime,
  Comment,
  ExpandMore,
  Update as UpdateIcon,
  CheckCircle,
  Pending,
  Link as LinkIcon,
  Search,
  FilterList,
} from '@mui/icons-material';
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
// Enhanced styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const StatusTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 8,
  padding: 4,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: 12,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const StatusTab = styled(Button)(({ theme, active }) => ({
  minHeight: 48,
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  flex: 1,
  transition: 'all 0.2s ease',
  ...(active ? {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  } : {
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
    },
  }),
}));

const ConsultationCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: 16,
  padding: 24,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '16px 16px 0 0',
  },
}));

const DetailSection = styled(Paper)(({ theme }) => ({
  padding: 20,
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backdropFilter: 'blur(10px)',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        bg: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        icon: <Pending sx={{ fontSize: '16px !important' }} />,
      },
      connected: {
        bg: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />,
      },
    };
    return configs[status] || configs.pending;
  };

  const config = getStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.color,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    borderRadius: 16,
    '& .MuiChip-icon': {
      color: config.color,
    },
  };
});

const PlatformChip = styled(Chip)(({ theme, platform }) => {
  const getPlatformConfig = (platform) => {
    const configs = {
      WhatsApp: {
        bg: '#25D366',
        color: '#ffffff',
        icon: <WhatsApp sx={{ fontSize: '16px !important' }} />,
      },
      Zoom: {
        bg: '#2D8CFF',
        color: '#ffffff',
        icon: <VideoCall sx={{ fontSize: '16px !important' }} />,
      },
      Phone: {
        bg: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        icon: <Phone sx={{ fontSize: '16px !important' }} />,
      },
    };
    return configs[platform] || configs.Phone;
  };

  const config = getPlatformConfig(platform);
  return {
    backgroundColor: config.bg,
    color: config.color,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    borderRadius: 16,
    '& .MuiChip-icon': {
      color: config.color,
    },
  };
});

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: 24,
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
}));

const TalkToAgronomistList = ({ data, searchQuery, onRefresh, pagination = null, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100] }) => {
    console.log(data)
  const theme = useTheme();
  const [consultations, setConsultations] = useState(data);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [updateDialog, setUpdateDialog] = useState({ open: false, consultation: null });
  const [newStatus, setNewStatus] = useState('');
  const [statusCounts, setStatusCounts] = useState({ pending: data.filter(item => !item.connected && !item.status).length, connected: data.filter(item => item.connected === true).length });

  const getStatus = (consultation) => {
    if (consultation.connected) return 'connected';
    return 'pending';
  };

  const getStatusDisplay = (consultation) => {
    const status = getStatus(consultation);
    return status === 'connected' ? 'Connected' : 'Pending';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  console.log(consultations)

  const filteredConsultations = data.filter(consultation => {
    const matchesSearch = 
      consultation.topic.toLowerCase().includes(searchValue.toLowerCase()) ||
      consultation.platform.toLowerCase().includes(searchValue.toLowerCase()) ||
      consultation.expert.toLowerCase().includes(searchValue.toLowerCase()) ||
      consultation.user.first_name.toLowerCase().includes(searchValue.toLowerCase()) ||
      consultation.user.last_name.toLowerCase().includes(searchValue.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || getStatus(consultation) === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  console.log(filteredConsultations)
  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const handleUpdateStatus = (consultation) => {
    setUpdateDialog({ open: true, consultation });
    setNewStatus(getStatus(consultation));
  };

  const handleStatusUpdate = async (consultation) => {
    console.log(consultation)
      try {
        const userId = localStorage.getItem("user_id");
        
        const payload = {
          ide: consultation.id,
          connected: true? newStatus==="connected": false ,
          user_id: userId,
        };
        const response = await apiService.put(
          `${API_ENDPOINTS.UPDATE_consultation_status}`,
          payload
        )
        if (response?.success) {
          await onRefresh() // Refresh the orders list
          setNewStatus(getStatus(consultation));
        }
      } catch (error) {
        console.error("Error updating order status:", error)
      } finally {
        setUpdateDialog({ open: false, consultation: null });
      }
    }
//   const handleStatusUpdate = () => {
//     // API call would go here
//     console.log('Updating status:', updateDialog.consultation.id, 'to', newStatus);
//     setUpdateDialog({ open: false, consultation: null });
//   };

  const ConsultationCardComponent = ({ consultation }) => {
    const isExpanded = expandedCards.has(consultation.id);
    const status = getStatus(consultation);

    return (
      <ConsultationCard>
        <Box onClick={() => toggleExpanded(consultation.id)}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
               
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                  }}
                >
                  {consultation?.user?.firstInitial?.charAt(0) || ''}
                  {consultation?.user?.lastInitial?.charAt(0) || ''}
                </Avatar>

                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {consultation?.user?.firstInitial || ''} {consultation?.user?.lastInitial || ''}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {consultation?.user?.village || ''}, {consultation?.user?.district || ''}
                  </Typography>
                </Box>
              </Box>
            <IconButton
              sx={{
                transition: 'transform 0.3s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>

          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Topic
                </Typography>
                <Chip
                  label={consultation.topic}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Platform
                </Typography>
                <PlatformChip
                  label={consultation.platform}
                  platform={consultation.platform}
                  size="small"
                  icon={consultation.platform === 'WhatsApp' ? <WhatsApp /> : <VideoCall />}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <StatusChip
                  label={getStatusDisplay(consultation)}
                  status={status}
                  size="small"
                  icon={status === 'connected' ? <CheckCircle /> : <Pending />}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scheduled
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(consultation.date)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(consultation.time)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Contact Information
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">{consultation.user.email || 'N/A'}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">{consultation.user.mobile_number}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {consultation.user.village}, {consultation.user.district}, {consultation.user.state} - {consultation.user.pincode}
                    </Typography>
                  </Box>
                </Stack>
              </DetailSection>
            </Grid>

            {/* Consultation Details */}
            <Grid item xs={12} md={6}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Agriculture color="success" />
                  <Typography variant="h6" fontWeight={600}>
                    Consultation Details
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Expert Type
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {consultation.expert}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(consultation.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </DetailSection>
            </Grid>

            {/* Comments */}
            {consultation.comments && (
              <Grid item xs={12}>
                <DetailSection elevation={0}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Comment color="info" />
                    <Typography variant="h6" fontWeight={600}>
                      Comments
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      fontStyle: 'italic',
                    }}
                  >
                    {consultation.comments}
                  </Typography>
                </DetailSection>
              </Grid>
            )}

            {/* Actions */}
            <Grid item xs={12}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={600}>
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<UpdateIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(consultation);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Update Status
                    </Button>
                    {/* {consultation.platform === 'Zoom' && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<VideoCall />}
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Join video call');
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                        }}
                      >
                        Join Call
                      </Button>
                    )} */}
                  </Stack>
                </Box>
              </DetailSection>
            </Grid>
          </Grid>
        </Collapse>
      </ConsultationCard>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Agronomist Consultations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track farmer consultation requests
        </Typography>
      </Box>

      {/* Status Tabs */}
      <StatusTabs mb={3}>
        <StatusTab
          active={statusFilter === 'pending'}
          onClick={() => setStatusFilter('pending')}
          startIcon={<Badge badgeContent={statusCounts.pending} color="error"><Pending /></Badge>}
        >
          Pending
        </StatusTab>
        <StatusTab
          active={statusFilter === 'connected'}
          onClick={() => setStatusFilter('connected')}
          startIcon={<Badge badgeContent={statusCounts.connected} color="success"><CheckCircle /></Badge>}
        >
          Connected
        </StatusTab>
      </StatusTabs>

{/* Search */}
<SearchContainer
  sx={{
    backgroundColor: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(255,255,255,0.8)',
    border: (theme) =>
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255,255,255,0.12)'
        : `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    p: 2,
    boxShadow: (theme) =>
      theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.6)' : theme.shadows[3],
    mb: 3,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.3s ease',
  }}
>
  <Box sx={{ position: 'relative' }}>
    <Search
      sx={{
        position: 'absolute',
        left: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        color: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.6)'
            : theme.palette.text.secondary,
      }}
    />
    <input
      type="text"
      placeholder="Search by farmer name, topic, platform, or expert type..."
      value={searchValue}
      onChange={(e) => setSearchValue(e.target.value)}
      style={{
        width: '100%',
        padding: '16px 16px 16px 48px',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: 12,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(255,255,255,0.9)',
        color:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.87)'
            : 'rgba(0,0,0,0.87)',
        backdropFilter: 'blur(10px)',
        fontSize: '1rem',
        outline: 'none',
        transition: 'all 0.2s ease',
      }}
    />
  </Box>
</SearchContainer>



      {/* Results Summary and row dropdown */}
      <Box mb={3} display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={2}>
       
        {pagination && onPageSizeChange && (
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pagination.page_size} label="Rows" onChange={onPageSizeChange}>
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Consultations List */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={40} />
        </Box>
      ) : filteredConsultations.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Agriculture sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No consultations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={3}>
            {filteredConsultations.map((consultation) => (
              <ConsultationCardComponent key={consultation.id} consultation={consultation} />
            ))}
          </Stack>
          {pagination && onPageChange && pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex',alignItems: "center", justifyContent: 'center', mt: 3 }}>
              <Pagination count={pagination.total_pages} page={pagination.current_page} onChange={onPageChange} color="primary" />
            </Box>
          )}
          {/* <Typography variant="body2" color="text.secondary">
          {pagination?.total_items != null
            ? (pagination.total_items > 0
                ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
                : 'No consultations')
            : `Showing ${filteredConsultations.length} ${statusFilter} consultations`}
        </Typography> */}
           
        </>
      )}
      <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    mt: 3,
  }}
>
  <Typography variant="body2" color="text.secondary">
    {pagination.total_items > 0
      ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(
          pagination.current_page * pagination.page_size,
          pagination.total_items
        )} of ${pagination.total_items}`
      : "No courses"}
  </Typography>
</Box>

      {/* Update Status Dialog */}
      <Dialog open={updateDialog.open} onClose={() => setUpdateDialog({ open: false, consultation: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Update Consultation Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="connected">Connected</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog({ open: false, consultation: null })}>
            Cancel
          </Button>
          <Button onClick={() => handleStatusUpdate(updateDialog.consultation)} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TalkToAgronomistList;